// polyfill
import 'intersection-observer';
import 'focus-visible';

import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Router } from 'react-router';
import { createBrowserHistory } from 'history';
import { Provider } from 'react-redux';
import { loadableReady } from '@loadable/component';
import { StyledTheme } from '@prisjakt/ui-base';
import after from 'lodash/after';
import throttle from 'lodash/throttle';
import smoothscroll from 'smoothscroll-polyfill';

import translator from '@prisjakt/ui-translate/src/translator';

import './public-path';
import { AppContextContainer } from '@prisjakt/ui-app-context';
import addPerformanceTiming from './performance-reporter';
import addWebVitalReport from './web-vitals-reporter';
import store from './store';
import FetchContext from '../components/fetch-context';
import { ViewportContextContainer } from '../components/viewport/context';
import { sentryPromise, captureException } from './sentry';
import { AdContextContainerWithExperiments } from '../components/ads/context';
import AdServiceContext from '../components/ads/services/context';
import App from '../app';
import ExperimentContextContainer from './experiments';
import {
  loadLocalStorageState,
  saveStateToLocalStorage,
} from './local-storage';
import { SearchBarProvider } from 'src/components/search-bar/search-bar-context';

import query from './query';

smoothscroll.polyfill();

const history = createBrowserHistory();

window.__history = history;

window.__getState = store.getState;

window.APP_GLOBALS = {
  t: translator(window.TRANSLATIONS, window.CLIENT_MARKET.locale),
};

addWebVitalReport('CLS');
addWebVitalReport('FID');
addWebVitalReport('LCP');

class AppWithViewport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      initialRender: window.SERVER_SIDE_RENDER_ROUTES === false,
    };
  }

  fetchContextValue = {
    query,
    ...window.CONTEXTS.fetch,
  };

  componentDidMount() {
    if (this.state.initialRender) {
      this.setState({
        initialRender: false,
      });
    }
  }

  render() {
    return (
      <AppContextContainer
        renderMode={window.RENDER_CONTEXT_MODE}
        market={window.CLIENT_MARKET}
        locale={window.LOCALE}
        translations={window.TRANSLATIONS}
        sentryPromise={sentryPromise}
      >
        <StyledTheme>
          <FetchContext.Provider value={this.fetchContextValue}>
            <Provider store={store}>
              <ExperimentContextContainer>
                <AdContextContainerWithExperiments>
                  <AdServiceContext.Provider>
                    <ViewportContextContainer>
                      <SearchBarProvider>
                        <Router history={history}>
                          <App callback={this.props.callback} />
                        </Router>
                      </SearchBarProvider>
                    </ViewportContextContainer>
                  </AdServiceContext.Provider>
                </AdContextContainerWithExperiments>
              </ExperimentContextContainer>
            </Provider>
          </FetchContext.Provider>
        </StyledTheme>
      </AppContextContainer>
    );
  }
}

addPerformanceTiming('startHydration');

const renderAppCallback = () => {
  addPerformanceTiming('hydrationCompleted');

  const {
    me,
    session,
    trackingSettings,
    gaClientId,
    pulseEnvironmentId,
  } = window.SESSION_DATA;

  window.PRE_TRACK_CLICKS = false;

  store.dispatch({
    type: 'SESSION_RECEIVE',
    session,
    privacyCookie: trackingSettings,
    clientId: gaClientId,
    pulseEnvironmentId,
  });

  if (me) {
    const companies =
      me && me.analyticsAccess
        ? [
            ...(me.analyticsAccess.stores || []),
            ...(me.analyticsAccess.brands || []),
          ].map(company => ({
            id: company.id,
            name: company.name,
          }))
        : undefined;

    store.dispatch({
      type: 'USER_RECEIVE',
      me: me ? { ...me, companies } : undefined,
    });
  }

  store.dispatch({
    type: 'APP_STARTUP',
    market: window.CLIENT_MARKET.code,
    release: window.RELEASE,
  });

  loadLocalStorageState(store);

  store.subscribe(
    throttle(() => {
      saveStateToLocalStorage(store);
    }, 1000)
  );

  if (/[&?]_verify_complete\b/.test(window.location.search)) {
    const paramValue = window.location.search.match(
      /[&?]_verify_complete=([^&]+)/
    );
    const mode = paramValue.length > 1 ? paramValue[1] : null;
    store.dispatch({
      type: 'VERIFY_ACCOUNT_COMPLETE',
      mode,
    });
  }
};

const renderApp = () => {
  let root;
  const container = document.getElementById('root');

  if (!root) {
    // Don't need to call `render` here because `hydrateRoot` will take care of it
    root = hydrateRoot(
      container,
      <AppWithViewport callback={renderAppCallback} />,
      {
        onRecoverableError(error) {
          captureException(error);
        },
      }
    );
  } else {
    root.render(<AppWithViewport callback={renderAppCallback} />);
  }
};

if (!window.TRANSLATIONS) {
  throw new Error('Missing translations, aborting hydrate');
}

const done = after(2, renderApp);

window.sessionReady(done);
loadableReady(done);
